import DownloadIcon from "@mui/icons-material/Download";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import SearchIcon from "@mui/icons-material/Search";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Button,
  ButtonGroup,
  Card,
  CardContent,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableFooter,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { FC, useCallback, useMemo, useState } from "react";
import { updateMensalidadeQuitada } from "src/apis/company";
import {
  FinancialReport,
  PendenciaEmpresa,
  getFinancialReport,
} from "src/apis/financial";
import { getApiErrorMessage } from "src/apis/prontuario";
import PageContainer from "src/pages/components/PageContainer";
import StartAndEndDateFilters from "src/pages/components/StartAndEndDateFilters";
import { isUserAdministrator } from "src/utils/auth";
import { formatDateToBR, getFirstDayOfMonth, getToday } from "src/utils/date";

const formatCurrency = (value: number): string =>
  new Intl.NumberFormat("pt-BR", { style: "currency", currency: "BRL" }).format(value ?? 0);

const escapeCell = (value: string | number | boolean | null | undefined): string => {
  const str = String(value ?? "");
  return `"${str.replace(/"/g, '""')}"`;
};

const buildCsvBlob = (rows: string[][]): Blob => {
  const content = rows.map((row) => row.map(escapeCell).join(";")).join("\n");
  return new Blob(["﻿" + content], { type: "text/csv;charset=utf-8;" });
};

const triggerDownload = (blob: Blob, filename: string): void => {
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = filename;
  anchor.click();
  URL.revokeObjectURL(url);
};

const computeConsolidado = (
  empresaId: number,
  mensalidadePendente: number,
  sessoesMap: Map<number, number>,
  valorMap: Map<number, number>
): number =>
  (sessoesMap.get(empresaId) ?? 0) * (valorMap.get(empresaId) ?? 0) + mensalidadePendente;

const resolveSessionStatus = (
  pago: boolean,
  responsavel: "Empresa" | "Colaborador",
  empresaId: number,
  quitadas: Set<number>
): "Pago" | "Pendente" =>
  responsavel === "Empresa"
    ? quitadas.has(empresaId) ? "Pago" : "Pendente"
    : pago ? "Pago" : "Pendente";

const exportToCsv = (report: FinancialReport, sessoesEmpresa: Map<number, number>, valorSessaoEmpresa: Map<number, number>, quitadas: Set<number>): void => {
  const rows: string[][] = [
    ["=== RESUMO FINANCEIRO ==="],
    ["Total a receber de empresas", String(report.totais.total_a_receber_empresas)],
    ["Total a receber de colaboradores", String(report.totais.total_a_receber_colaboradores)],
    ["Total geral a receber (previsão)", String(report.totais.total_a_receber_previsao)],
    ["Total a pagar psicólogos", String(report.totais.total_a_pagar_psicologos)],
    ["Saldo", String(report.totais.saldo)],
    [],
    ["=== PENDÊNCIAS POR EMPRESA ==="],
    ["Empresa", "Sessões pendentes", "Valor da sessão", "Valor mensal pendente", "Valor mensal consolidado"],
    ...report.pendencias_empresas.map((p) => [
      p.empresa_nome,
      String(sessoesEmpresa.get(p.empresa_id) ?? 0),
      String(valorSessaoEmpresa.get(p.empresa_id) ?? 0),
      String(p.mensalidade_pendente),
      String(computeConsolidado(p.empresa_id, p.mensalidade_pendente, sessoesEmpresa, valorSessaoEmpresa)),
    ]),
    [],
    ["=== A PAGAR — PSICÓLOGOS ==="],
    ["Psicólogo", "E-mail", "Sessões", "Valor/sessão", "Total a pagar"],
    ...report.a_pagar_psicologos.map((p) => [
      p.psicologo_nome,
      p.psicologo_email ?? "",
      String(p.total_sessoes),
      String(p.valor_por_sessao),
      String(p.valor_total_a_pagar),
    ]),
    [],
    ["=== COLABORADORES DEVEDORES ==="],
    ["Colaborador", "Empresa", "Sessões", "Total devido"],
    ...report.devedores_colaboradores.map((d) => [
      d.colaborador_nome,
      d.empresa_nome,
      String(d.sessoes_pendentes),
      String(d.valor_total_pendente),
    ]),
    [],
    ["=== LISTA DE RECEBIMENTOS ==="],
    ["Data", "Colaborador", "Psicólogo", "Empresa", "Valor", "Responsável", "Status"],
    ...report.lista_recebimentos.map((s) => [
      s.data,
      s.colaborador_nome,
      s.psicologo_nome,
      s.empresa_nome,
      String(s.valor),
      s.responsavel_pagamento,
      resolveSessionStatus(s.pago, s.responsavel_pagamento, s.empresa_id, quitadas),
    ]),
    [],
    ["=== CONTAS A PAGAR POR SESSÃO ==="],
    ["Data", "Colaborador", "Psicólogo", "Empresa", "Valor sessão", "Valor psicólogo"],
    ...report.lista_contas_a_pagar.map((s) => [
      s.data,
      s.colaborador_nome,
      s.psicologo_nome,
      s.empresa_nome,
      String(s.valor_sessao),
      String(s.valor_psicologo),
    ]),
  ];

  triggerDownload(buildCsvBlob(rows), "balanco-financeiro.csv");
};

const exportToExcel = (report: FinancialReport, sessoesEmpresa: Map<number, number>, valorSessaoEmpresa: Map<number, number>, quitadas: Set<number>): void => {
  const tableHtml = `
    <html xmlns:o="urn:schemas-microsoft-com:office:office" xmlns:x="urn:schemas-microsoft-com:office:excel">
    <head><meta charset="UTF-8"></head>
    <body>
      <table border="1">
        <tr><th colspan="7">RESUMO FINANCEIRO</th></tr>
        <tr><td>Total a receber de empresas</td><td>${report.totais.total_a_receber_empresas}</td></tr>
        <tr><td>Total a receber de colaboradores</td><td>${report.totais.total_a_receber_colaboradores}</td></tr>
        <tr><td>Total geral a receber (previsão)</td><td>${report.totais.total_a_receber_previsao}</td></tr>
        <tr><td>Total a pagar psicólogos</td><td>${report.totais.total_a_pagar_psicologos}</td></tr>
        <tr><td>Saldo</td><td>${report.totais.saldo}</td></tr>
        <tr><td colspan="7"></td></tr>

        <tr><th colspan="7">PENDÊNCIAS POR EMPRESA</th></tr>
        <tr><th>Empresa</th><th>Sessões pendentes</th><th>Valor da sessão</th><th>Valor mensal pendente</th><th>Valor mensal consolidado</th></tr>
        ${report.pendencias_empresas
          .map((p) => `<tr><td>${p.empresa_nome}</td><td>${sessoesEmpresa.get(p.empresa_id) ?? 0}</td><td>${formatCurrency(valorSessaoEmpresa.get(p.empresa_id) ?? 0)}</td><td>${formatCurrency(p.mensalidade_pendente)}</td><td><strong>${formatCurrency(computeConsolidado(p.empresa_id, p.mensalidade_pendente, sessoesEmpresa, valorSessaoEmpresa))}</strong></td></tr>`)
          .join("")}
        <tr><td colspan="7"></td></tr>

        <tr><th colspan="7">A PAGAR — PSICÓLOGOS</th></tr>
        <tr><th>Psicólogo</th><th>E-mail</th><th>Sessões</th><th>Valor/sessão</th><th>Total a pagar</th></tr>
        ${report.a_pagar_psicologos
          .map((p) => `<tr><td>${p.psicologo_nome}</td><td>${p.psicologo_email ?? ""}</td><td>${p.total_sessoes}</td><td>${p.valor_por_sessao}</td><td>${p.valor_total_a_pagar}</td></tr>`)
          .join("")}
        <tr><td colspan="7"></td></tr>

        <tr><th colspan="7">COLABORADORES DEVEDORES</th></tr>
        <tr><th>Colaborador</th><th>Empresa</th><th>Sessões</th><th>Total devido</th></tr>
        ${report.devedores_colaboradores
          .map((d) => `<tr><td>${d.colaborador_nome}</td><td>${d.empresa_nome}</td><td>${d.sessoes_pendentes}</td><td>${d.valor_total_pendente}</td></tr>`)
          .join("")}
        <tr><td colspan="7"></td></tr>

        <tr><th colspan="7">LISTA DE RECEBIMENTOS</th></tr>
        <tr><th>Data</th><th>Colaborador</th><th>Psicólogo</th><th>Empresa</th><th>Valor</th><th>Responsável</th><th>Status</th></tr>
        ${report.lista_recebimentos
          .map((s) => `<tr><td>${s.data}</td><td>${s.colaborador_nome}</td><td>${s.psicologo_nome}</td><td>${s.empresa_nome}</td><td>${s.valor}</td><td>${s.responsavel_pagamento}</td><td>${resolveSessionStatus(s.pago, s.responsavel_pagamento, s.empresa_id, quitadas)}</td></tr>`)
          .join("")}
        <tr><td colspan="7"></td></tr>

        <tr><th colspan="7">CONTAS A PAGAR POR SESSÃO</th></tr>
        <tr><th>Data</th><th>Colaborador</th><th>Psicólogo</th><th>Empresa</th><th>Valor sessão</th><th>Valor psicólogo</th></tr>
        ${report.lista_contas_a_pagar
          .map((s) => `<tr><td>${s.data}</td><td>${s.colaborador_nome}</td><td>${s.psicologo_nome}</td><td>${s.empresa_nome}</td><td>${s.valor_sessao}</td><td>${s.valor_psicologo}</td></tr>`)
          .join("")}
      </table>
    </body></html>
  `;

  const blob = new Blob([tableHtml], { type: "application/vnd.ms-excel;charset=utf-8" });
  triggerDownload(blob, "balanco-financeiro.xls");
};

const exportToPdf = (report: FinancialReport, sessoesEmpresa: Map<number, number>, valorSessaoEmpresa: Map<number, number>, quitadas: Set<number>): void => {
  const win = window.open("", "_blank");
  if (!win) return;

  const fmt = formatCurrency;

  win.document.write(`
    <html>
    <head>
      <meta charset="UTF-8">
      <title>Balanço Financeiro</title>
      <style>
        body { font-family: Arial, sans-serif; font-size: 12px; padding: 20px; }
        h1 { font-size: 18px; margin-bottom: 4px; }
        h2 { font-size: 14px; margin-top: 24px; margin-bottom: 8px; border-bottom: 1px solid #ccc; }
        table { width: 100%; border-collapse: collapse; margin-bottom: 16px; }
        th, td { border: 1px solid #ccc; padding: 6px 8px; text-align: left; }
        th { background: #f0f0f0; font-weight: bold; }
        .summary-grid { display: flex; flex-wrap: wrap; gap: 12px; margin-bottom: 16px; }
        .summary-card { border: 1px solid #ccc; padding: 12px 16px; border-radius: 4px; min-width: 160px; }
        .summary-card .label { font-size: 11px; color: #555; }
        .summary-card .value { font-size: 16px; font-weight: bold; }
      </style>
    </head>
    <body>
      <h1>Balanço Financeiro</h1>
      <p>Período: ${formatDateToBR(report.periodo.start)} a ${formatDateToBR(report.periodo.end)} — Total de sessões: ${report.total_sessoes}</p>

      <h2>Resumo financeiro</h2>
      <div class="summary-grid">
        <div class="summary-card"><div class="label">Total a receber (empresas)</div><div class="value">${fmt(report.totais.total_a_receber_empresas)}</div></div>
        <div class="summary-card"><div class="label">Total a receber (colaboradores)</div><div class="value">${fmt(report.totais.total_a_receber_colaboradores)}</div></div>
        <div class="summary-card"><div class="label">Total geral a receber (previsão)</div><div class="value">${fmt(report.totais.total_a_receber_previsao)}</div></div>
        <div class="summary-card"><div class="label">Total a pagar psicólogos</div><div class="value">${fmt(report.totais.total_a_pagar_psicologos)}</div></div>
        <div class="summary-card"><div class="label">Saldo</div><div class="value">${fmt(report.totais.saldo)}</div></div>
      </div>

      <h2>Pendências por empresa</h2>
      <table><thead><tr><th>Empresa</th><th>Sessões pendentes</th><th>Valor da sessão</th><th>Valor mensal pendente</th><th>Valor mensal consolidado</th></tr></thead><tbody>
        ${report.pendencias_empresas.map((p) => `<tr><td>${p.empresa_nome}</td><td>${sessoesEmpresa.get(p.empresa_id) ?? 0}</td><td>${fmt(valorSessaoEmpresa.get(p.empresa_id) ?? 0)}</td><td>${fmt(p.mensalidade_pendente)}</td><td><strong>${fmt(computeConsolidado(p.empresa_id, p.mensalidade_pendente, sessoesEmpresa, valorSessaoEmpresa))}</strong></td></tr>`).join("")}
      </tbody></table>

      <h2>A pagar — Psicólogos</h2>
      <table><thead><tr><th>Psicólogo</th><th>E-mail</th><th>Sessões</th><th>Valor/sessão</th><th>Total a pagar</th></tr></thead><tbody>
        ${report.a_pagar_psicologos.map((p) => `<tr><td>${p.psicologo_nome}</td><td>${p.psicologo_email ?? "-"}</td><td>${p.total_sessoes}</td><td>${fmt(p.valor_por_sessao)}</td><td>${fmt(p.valor_total_a_pagar)}</td></tr>`).join("")}
      </tbody></table>

      <h2>Colaboradores devedores</h2>
      <table><thead><tr><th>Colaborador</th><th>Empresa</th><th>Sessões</th><th>Total devido</th></tr></thead><tbody>
        ${report.devedores_colaboradores.map((d) => `<tr><td>${d.colaborador_nome}</td><td>${d.empresa_nome}</td><td>${d.sessoes_pendentes}</td><td>${fmt(d.valor_total_pendente)}</td></tr>`).join("")}
      </tbody></table>

      <h2>Lista de recebimentos</h2>
      <table><thead><tr><th>Data</th><th>Colaborador</th><th>Psicólogo</th><th>Empresa</th><th>Valor</th><th>Responsável</th><th>Status</th></tr></thead><tbody>
        ${report.lista_recebimentos.map((s) => `<tr><td>${formatDateToBR(s.data)}</td><td>${s.colaborador_nome}</td><td>${s.psicologo_nome}</td><td>${s.empresa_nome}</td><td>${fmt(s.valor)}</td><td>${s.responsavel_pagamento}</td><td>${resolveSessionStatus(s.pago, s.responsavel_pagamento, s.empresa_id, quitadas)}</td></tr>`).join("")}
      </tbody></table>

      <h2>Contas a pagar — por sessão</h2>
      <table><thead><tr><th>Data</th><th>Colaborador</th><th>Psicólogo</th><th>Empresa</th><th>Valor sessão</th><th>Valor psicólogo</th></tr></thead><tbody>
        ${report.lista_contas_a_pagar.map((s) => `<tr><td>${formatDateToBR(s.data)}</td><td>${s.colaborador_nome}</td><td>${s.psicologo_nome}</td><td>${s.empresa_nome}</td><td>${fmt(s.valor_sessao)}</td><td>${fmt(s.valor_psicologo)}</td></tr>`).join("")}
      </tbody></table>
    </body></html>
  `);
  win.document.close();
  win.print();
};

const ROWS_PER_PAGE = 10;

const getCurrentMonth = (): string => {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}`;
};

const RelatorioFinanceiroPage: FC = () => {
  const [startDate, setStartDate] = useState<string>(getFirstDayOfMonth());
  const [endDate, setEndDate] = useState<string>(getToday());
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string>("");
  const [report, setReport] = useState<FinancialReport | null>(null);
  const [page5, setPage5] = useState<number>(0);
  const [page6, setPage6] = useState<number>(0);
  const [mensalidadeModal, setMensalidadeModal] = useState<PendenciaEmpresa | null>(null);
  const [mensalidadeMes, setMensalidadeMes] = useState<string>(getCurrentMonth());
  const [mensalidadeLoading, setMensalidadeLoading] = useState<boolean>(false);
  const [mensalidadesQuitadas, setMensalidadesQuitadas] = useState<Set<number>>(new Set());

  const fetchReport = useCallback(async () => {
    setLoading(true);
    setError("");
    setReport(null);
    try {
      const data = await getFinancialReport(startDate, endDate);
      setReport(data);
      setPage5(0);
      setPage6(0);
    } catch (err: unknown) {
      setError(getApiErrorMessage(err, "Não foi possível carregar o relatório financeiro."));
    } finally {
      setLoading(false);
    }
  }, [startDate, endDate]);

  const handleConfirmarMensalidade = async (): Promise<void> => {
    if (!mensalidadeModal) return;
    setMensalidadeLoading(true);
    try {
      await updateMensalidadeQuitada(mensalidadeModal.empresa_id, true);
      setMensalidadesQuitadas((prev) => new Set(prev).add(mensalidadeModal.empresa_id));
      setMensalidadeModal(null);
    } catch (err: unknown) {
      setError(getApiErrorMessage(err, "Não foi possível registrar a quitação da mensalidade."));
      setMensalidadeModal(null);
    } finally {
      setMensalidadeLoading(false);
    }
  };

  // Conta sessões e captura valor unitário por empresa a partir da lista de recebimentos
  const { sessoesEmpresa, valorSessaoEmpresa } = useMemo(() => {
    const count = new Map<number, number>();
    const unitValue = new Map<number, number>();
    for (const s of report?.lista_recebimentos ?? []) {
      if (s.responsavel_pagamento === "Empresa") {
        count.set(s.empresa_id, (count.get(s.empresa_id) ?? 0) + 1);
        if (!unitValue.has(s.empresa_id) && s.valor > 0) {
          unitValue.set(s.empresa_id, s.valor);
        }
      }
    }
    return { sessoesEmpresa: count, valorSessaoEmpresa: unitValue };
  }, [report]);

  const totais = report?.totais;

  // Recalcula a receber por responsável a partir da lista de recebimentos,
  // garantindo que sessões de empresa não contaminem o total de colaboradores.
  const totaisCorrigidos = useMemo(() => {
    if (!report) return null;

    const aReceberColaborador = report.lista_recebimentos
      .filter((s) => s.responsavel_pagamento === "Colaborador" && !s.pago)
      .reduce((sum, s) => sum + s.valor, 0);

    const sessoesEmpresaValor = report.lista_recebimentos
      .filter((s) => s.responsavel_pagamento === "Empresa")
      .reduce((sum, s) => sum + s.valor, 0);

    const mensalidadesPendentes = report.pendencias_empresas
      .reduce((sum, p) => sum + p.mensalidade_pendente, 0);

    const aReceberEmpresas = sessoesEmpresaValor + mensalidadesPendentes;
    const totalGeral = aReceberEmpresas + aReceberColaborador;

    return { aReceberEmpresas, aReceberColaborador, totalGeral };
  }, [report]);

  const summaryCards = totais && totaisCorrigidos
    ? [
        { label: "Total a receber (empresas)", value: totaisCorrigidos.aReceberEmpresas, color: "warning.main", caption: "Sessões de empresa pendentes + mensalidades não quitadas" },
        { label: "Total a receber (colaboradores)", value: totaisCorrigidos.aReceberColaborador, color: "warning.main", caption: "Sessões particulares não pagas" },
        { label: "Total geral a receber (previsão)", value: totaisCorrigidos.totalGeral, color: "primary.main", caption: undefined },
        { label: "Total a pagar psicólogos", value: totais.total_a_pagar_psicologos, color: "error.main", caption: undefined },
        { label: "Valores recebidos (empresas)", value: totais.total_recebido_empresas, color: "success.main", caption: "Mensalidades quitadas no período" },
        { label: "Valores recebidos (colaboradores)", value: totais.total_recebido_colaboradores, color: "success.main", caption: "Sessões particulares já pagas" },
        {
          label: "Saldo",
          value: totais.saldo,
          color: totais.saldo >= 0 ? "success.main" : "error.main",
          caption: undefined,
        },
      ]
    : [];

  const totalPendenciasEmpresa = report?.pendencias_empresas.reduce(
    (sum, p) => ({
      sessoes: sum.sessoes + (sessoesEmpresa.get(p.empresa_id) ?? 0),
      mensalidade: sum.mensalidade + p.mensalidade_pendente,
      consolidado: sum.consolidado + computeConsolidado(p.empresa_id, p.mensalidade_pendente, sessoesEmpresa, valorSessaoEmpresa),
    }),
    { sessoes: 0, mensalidade: 0, consolidado: 0 }
  );

  const totalPsicologo = report?.a_pagar_psicologos.reduce(
    (sum, p) => ({ sessoes: sum.sessoes + p.total_sessoes, valor: sum.valor + p.valor_total_a_pagar }),
    { sessoes: 0, valor: 0 }
  );

  const totalDevedores = report?.devedores_colaboradores.reduce(
    (sum, d) => ({ sessoes: sum.sessoes + d.sessoes_pendentes, valor: sum.valor + d.valor_total_pendente }),
    { sessoes: 0, valor: 0 }
  );

  const recebimentosPage = report?.lista_recebimentos.slice(
    page5 * ROWS_PER_PAGE,
    page5 * ROWS_PER_PAGE + ROWS_PER_PAGE
  ) ?? [];

  const contasApagarPage = report?.lista_contas_a_pagar.slice(
    page6 * ROWS_PER_PAGE,
    page6 * ROWS_PER_PAGE + ROWS_PER_PAGE
  ) ?? [];

  return (
    <PageContainer title="Balanço Financeiro" guard={isUserAdministrator}>
      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, alignItems: "center" }}>
        <StartAndEndDateFilters
          startDate={startDate}
          setStartDate={setStartDate}
          endDate={endDate}
          setEndDate={setEndDate}
        />
        <Button
          variant="contained"
          startIcon={loading ? undefined : <SearchIcon />}
          disabled={loading || !startDate || !endDate}
          onClick={fetchReport}
        >
          {loading ? <CircularProgress size="1.25rem" color="inherit" /> : "Gerar relatório"}
        </Button>
      </Box>

      {Boolean(error) && <Alert severity="error">{error}</Alert>}

      {report && (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
          <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
            <ButtonGroup variant="outlined" size="small">
              <Button startIcon={<DownloadIcon />} onClick={() => exportToCsv(report, sessoesEmpresa, valorSessaoEmpresa, mensalidadesQuitadas)}>
                CSV
              </Button>
              <Button startIcon={<DownloadIcon />} onClick={() => exportToExcel(report, sessoesEmpresa, valorSessaoEmpresa, mensalidadesQuitadas)}>
                Excel
              </Button>
              <Button startIcon={<DownloadIcon />} onClick={() => exportToPdf(report, sessoesEmpresa, valorSessaoEmpresa, mensalidadesQuitadas)}>
                PDF
              </Button>
            </ButtonGroup>
          </Box>

          <Accordion defaultExpanded>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography fontWeight={700}>
                Resumo financeiro — {report.total_sessoes} sessões no período
              </Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Grid container spacing={2} alignItems="stretch">
                {summaryCards.map((card) => (
                  <Grid item xs={12} sm={6} md={4} lg={12 / 7} key={card.label} sx={{ display: "flex" }}>
                    <Card variant="outlined" sx={{ width: "100%", display: "flex", flexDirection: "column" }}>
                      <CardContent sx={{ flex: 1, display: "flex", flexDirection: "column", justifyContent: "space-between" }}>
                        <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                          {card.label}
                        </Typography>
                        <Typography variant="h6" fontWeight={700} color={card.color}>
                          {formatCurrency(card.value)}
                        </Typography>
                        <Typography variant="caption" color="text.secondary" sx={{ minHeight: "1.2em", display: "block" }}>
                          {card.caption ?? ""}
                        </Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                ))}
              </Grid>
            </AccordionDetails>
          </Accordion>

          <Accordion>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography fontWeight={700}>
                Pendências por empresa ({report.pendencias_empresas.length})
              </Typography>
            </AccordionSummary>
            <AccordionDetails>
              <TableContainer sx={{ overflowX: "auto" }}>
                <Table sx={{ minWidth: 650 }}>
                  <TableHead>
                    <TableRow>
                      <TableCell><strong>Empresa</strong></TableCell>
                      <TableCell align="right"><strong>Sessões pendentes</strong></TableCell>
                      <TableCell align="right"><strong>Valor da sessão</strong></TableCell>
                      <TableCell align="right"><strong>Valor mensal pendente</strong></TableCell>
                      <TableCell align="right"><strong>Valor mensal consolidado</strong></TableCell>
                      <TableCell align="center"><strong>Mensalidade quitada</strong></TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {[...report.pendencias_empresas]
                      .sort((a, b) => b.mensalidade_pendente - a.mensalidade_pendente)
                      .map((p) => {
                        const quitada = mensalidadesQuitadas.has(p.empresa_id);
                        const valorUnitario = valorSessaoEmpresa.get(p.empresa_id) ?? 0;
                        return (
                          <TableRow key={p.empresa_id}>
                            <TableCell>{p.empresa_nome}</TableCell>
                            <TableCell align="right">{sessoesEmpresa.get(p.empresa_id) ?? 0}</TableCell>
                            <TableCell align="right">{formatCurrency(valorUnitario)}</TableCell>
                            <TableCell align="right">{formatCurrency(p.mensalidade_pendente)}</TableCell>
                            <TableCell align="right"><strong>{formatCurrency(computeConsolidado(p.empresa_id, p.mensalidade_pendente, sessoesEmpresa, valorSessaoEmpresa))}</strong></TableCell>
                            <TableCell align="center">
                              <Tooltip title={quitada ? "Mensalidade já quitada nesta sessão" : "Registrar quitação da mensalidade"}>
                                <span>
                                  <Checkbox
                                    checked={quitada}
                                    disabled={quitada}
                                    color="success"
                                    onChange={() => {
                                      setMensalidadeMes(getCurrentMonth());
                                      setMensalidadeModal(p);
                                    }}
                                  />
                                </span>
                              </Tooltip>
                            </TableCell>
                          </TableRow>
                        );
                      })}
                  </TableBody>
                  {totalPendenciasEmpresa && (
                    <TableFooter>
                      <TableRow>
                        <TableCell><strong>Total</strong></TableCell>
                        <TableCell align="right"><strong>{totalPendenciasEmpresa.sessoes}</strong></TableCell>
                        <TableCell />
                        <TableCell align="right"><strong>{formatCurrency(totalPendenciasEmpresa.mensalidade)}</strong></TableCell>
                        <TableCell align="right"><strong>{formatCurrency(totalPendenciasEmpresa.consolidado)}</strong></TableCell>
                        <TableCell />
                      </TableRow>
                    </TableFooter>
                  )}
                </Table>
              </TableContainer>
            </AccordionDetails>
          </Accordion>

          <Accordion>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography fontWeight={700}>
                A pagar — Psicólogos ({report.a_pagar_psicologos.length})
              </Typography>
            </AccordionSummary>
            <AccordionDetails>
              <TableContainer sx={{ overflowX: "auto" }}>
                <Table sx={{ minWidth: 650 }}>
                  <TableHead>
                    <TableRow>
                      <TableCell><strong>Psicólogo</strong></TableCell>
                      <TableCell><strong>E-mail</strong></TableCell>
                      <TableCell align="right"><strong>Sessões</strong></TableCell>
                      <TableCell align="right"><strong>Valor/sessão</strong></TableCell>
                      <TableCell align="right"><strong>Total a pagar</strong></TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {report.a_pagar_psicologos.map((p) => (
                      <TableRow key={p.psicologo_id}>
                        <TableCell>{p.psicologo_nome}</TableCell>
                        <TableCell>{p.psicologo_email ?? "-"}</TableCell>
                        <TableCell align="right">{p.total_sessoes}</TableCell>
                        <TableCell align="right">{formatCurrency(p.valor_por_sessao)}</TableCell>
                        <TableCell align="right">{formatCurrency(p.valor_total_a_pagar)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                  {totalPsicologo && (
                    <TableFooter>
                      <TableRow>
                        <TableCell colSpan={2}><strong>Total</strong></TableCell>
                        <TableCell align="right"><strong>{totalPsicologo.sessoes}</strong></TableCell>
                        <TableCell />
                        <TableCell align="right"><strong>{formatCurrency(totalPsicologo.valor)}</strong></TableCell>
                      </TableRow>
                    </TableFooter>
                  )}
                </Table>
              </TableContainer>
            </AccordionDetails>
          </Accordion>

          <Accordion>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography fontWeight={700}>
                Colaboradores devedores ({report.devedores_colaboradores.length})
              </Typography>
            </AccordionSummary>
            <AccordionDetails>
              <TableContainer sx={{ overflowX: "auto" }}>
                <Table sx={{ minWidth: 650 }}>
                  <TableHead>
                    <TableRow>
                      <TableCell><strong>Colaborador</strong></TableCell>
                      <TableCell><strong>Empresa</strong></TableCell>
                      <TableCell align="right"><strong>Sessões</strong></TableCell>
                      <TableCell align="right"><strong>Total devido</strong></TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {report.devedores_colaboradores.map((d) => (
                      <TableRow key={d.colaborador_id}>
                        <TableCell>{d.colaborador_nome}</TableCell>
                        <TableCell>{d.empresa_nome}</TableCell>
                        <TableCell align="right">{d.sessoes_pendentes}</TableCell>
                        <TableCell align="right">{formatCurrency(d.valor_total_pendente)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                  {totalDevedores && (
                    <TableFooter>
                      <TableRow>
                        <TableCell colSpan={2}><strong>Total</strong></TableCell>
                        <TableCell align="right"><strong>{totalDevedores.sessoes}</strong></TableCell>
                        <TableCell align="right"><strong>{formatCurrency(totalDevedores.valor)}</strong></TableCell>
                      </TableRow>
                    </TableFooter>
                  )}
                </Table>
              </TableContainer>
            </AccordionDetails>
          </Accordion>

          <Accordion>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography fontWeight={700}>
                Lista completa de recebimentos ({report.lista_recebimentos.length})
              </Typography>
            </AccordionSummary>
            <AccordionDetails>
              <TableContainer sx={{ overflowX: "auto" }}>
                <Table sx={{ minWidth: 650 }}>
                  <TableHead>
                    <TableRow>
                      <TableCell><strong>Data</strong></TableCell>
                      <TableCell><strong>Colaborador</strong></TableCell>
                      <TableCell><strong>Psicólogo</strong></TableCell>
                      <TableCell><strong>Empresa</strong></TableCell>
                      <TableCell align="right"><strong>Valor</strong></TableCell>
                      <TableCell><strong>Responsável</strong></TableCell>
                      <TableCell><strong>Status</strong></TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {recebimentosPage.map((s) => (
                      <TableRow key={s.service_id}>
                        <TableCell>{formatDateToBR(s.data)}</TableCell>
                        <TableCell>{s.colaborador_nome}</TableCell>
                        <TableCell>{s.psicologo_nome}</TableCell>
                        <TableCell>{s.empresa_nome}</TableCell>
                        <TableCell align="right">{formatCurrency(s.valor)}</TableCell>
                        <TableCell>{s.responsavel_pagamento}</TableCell>
                        <TableCell>
                          {(() => {
                            const status = resolveSessionStatus(s.pago, s.responsavel_pagamento, s.empresa_id, mensalidadesQuitadas);
                            return (
                              <Chip
                                size="small"
                                label={status}
                                color={status === "Pago" ? "success" : "warning"}
                              />
                            );
                          })()}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
              <TablePagination
                component="div"
                count={report.lista_recebimentos.length}
                page={page5}
                onPageChange={(_, newPage) => setPage5(newPage)}
                rowsPerPage={ROWS_PER_PAGE}
                rowsPerPageOptions={[ROWS_PER_PAGE]}
                labelDisplayedRows={({ from, to, count }) => `${from}–${to} de ${count}`}
              />
            </AccordionDetails>
          </Accordion>

          <Accordion>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography fontWeight={700}>
                Contas a pagar — por sessão ({report.lista_contas_a_pagar.length})
              </Typography>
            </AccordionSummary>
            <AccordionDetails>
              <TableContainer sx={{ overflowX: "auto" }}>
                <Table sx={{ minWidth: 650 }}>
                  <TableHead>
                    <TableRow>
                      <TableCell><strong>Data</strong></TableCell>
                      <TableCell><strong>Colaborador</strong></TableCell>
                      <TableCell><strong>Psicólogo</strong></TableCell>
                      <TableCell><strong>Empresa</strong></TableCell>
                      <TableCell align="right"><strong>Valor sessão</strong></TableCell>
                      <TableCell align="right"><strong>Valor psicólogo</strong></TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {contasApagarPage.map((s) => (
                      <TableRow key={s.service_id}>
                        <TableCell>{formatDateToBR(s.data)}</TableCell>
                        <TableCell>{s.colaborador_nome}</TableCell>
                        <TableCell>{s.psicologo_nome}</TableCell>
                        <TableCell>{s.empresa_nome}</TableCell>
                        <TableCell align="right">{formatCurrency(s.valor_sessao)}</TableCell>
                        <TableCell align="right">{formatCurrency(s.valor_psicologo)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
              <TablePagination
                component="div"
                count={report.lista_contas_a_pagar.length}
                page={page6}
                onPageChange={(_, newPage) => setPage6(newPage)}
                rowsPerPage={ROWS_PER_PAGE}
                rowsPerPageOptions={[ROWS_PER_PAGE]}
                labelDisplayedRows={({ from, to, count }) => `${from}–${to} de ${count}`}
              />
            </AccordionDetails>
          </Accordion>
        </Box>
      )}

      <Dialog
        open={Boolean(mensalidadeModal)}
        onClose={() => !mensalidadeLoading && setMensalidadeModal(null)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Registrar quitação de mensalidade</DialogTitle>
        <DialogContent>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2, pt: 1 }}>
            <Typography variant="body2">
              Empresa: <strong>{mensalidadeModal?.empresa_nome}</strong>
            </Typography>
            <TextField
              label="Mês de referência"
              type="month"
              value={mensalidadeMes}
              onChange={(e) => setMensalidadeMes(e.target.value)}
              InputLabelProps={{ shrink: true }}
              fullWidth
              disabled={mensalidadeLoading}
            />
            <Typography variant="caption" color="text.secondary">
              Confirme o mês/ano ao qual se refere este pagamento antes de registrar a baixa.
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setMensalidadeModal(null)}
            disabled={mensalidadeLoading}
          >
            Cancelar
          </Button>
          <Button
            variant="contained"
            color="success"
            disabled={mensalidadeLoading || !mensalidadeMes}
            onClick={handleConfirmarMensalidade}
            startIcon={mensalidadeLoading ? <CircularProgress size="1rem" color="inherit" /> : undefined}
          >
            {mensalidadeLoading ? "Registrando..." : "Confirmar quitação"}
          </Button>
        </DialogActions>
      </Dialog>
    </PageContainer>
  );
};

export default RelatorioFinanceiroPage;
